import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router'
import { ConfirmActionModal } from '@/components/commons/confirm-action-modal'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowLeftIcon, CheckIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { EditingQaFlagsSpaces } from '@/features/photoset/components/editing-qa-flags-spaces'
import {
	PhotosetStepGridContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { ShootListMetadataSidebar } from '@/features/photoset/components/shoot-list-metadata-sidebar'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { useCurrentPhotosetSubRoute } from '@/features/photoset/hooks/use-current-photoset-sub-route'
import { useGetPhotosetSpacesList } from '@/features/photoset/queries/use-get-photoset-spaces-list'
import { useUpdateStepToCompleted } from '@/features/photoset/queries/use-update-step-to-completed'
import { useUpdateStepToQaReviewCompleted } from '@/features/photoset/queries/use-update-step-to-qa-review-completed'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import type { PhotosetURLParams } from '@/features/photoset/types.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetEditingQaFlagsStep = () => {
	const { t } = useTranslation()
	const [hasSidebar, setHasSidebar] = useState(true)
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const currentPhotosetSubRoute = useCurrentPhotosetSubRoute()

	const { data: spaces } = useGetPhotosetSpacesList({
		urlParams: {
			photosetId: urlParams.id
		}
	})

	//photoset store
	const initPhotosetSpaces = usePhotosetStore(
		(state) => state.initPhotosetSpaces
	)

	//mutations
	const {
		mutate: onUpdateStepToEditingQaCompleted,
		isPending: isUpdateStepToEditingQaCompleted
	} = useUpdateStepToQaReviewCompleted({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.IMAGES]
			})
		}
	})
	const {
		mutate: onUpdateStepToEditingQaSelection,
		isPending: isUpdateStepToEditingQaSelection
	} = useUpdateStepToCompleted({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.IMAGES]
			})
		}
	})

	//statuses
	const [showConfirmationModal, setShowConfirmationModal] = useState(false)

	useEffect(() => {
		if (spaces) {
			initPhotosetSpaces(spaces)
		}
	}, [spaces, initPhotosetSpaces])

	return (
		<>
			{showConfirmationModal && (
				<ConfirmActionModal
					headerTitle={t(
						'photoset:editingQaFlags.modal.header_title'
					)}
					isLoading={isUpdateStepToEditingQaCompleted}
					onClose={() =>
						setShowConfirmationModal(!showConfirmationModal)
					}
					onConfirm={() =>
						onUpdateStepToEditingQaCompleted({
							photosetId: urlParams.id
						})
					}
					paragraph={t('photoset:editingQaFlags.modal.subtitle')}
					title={t('photoset:editingQaFlags.modal.title')}
				/>
			)}

			<PageHero
				endSlot={<StepHeroControls hideSortControls />}
				subtitle={t('photoset:editingQaFlags.subtitle')}
				title={t('photoset:editingQaFlags.title')}
			/>

			<PhotosetStepWrapper>
				<PhotosetStepGridContainer>
					<EditingQaFlagsSpaces
						hasSidebar={hasSidebar}
						showSidebar={() => setHasSidebar(true)}
					/>
					{hasSidebar && (
						<>
							<Separator orientation='vertical' />
							<ShootListMetadataSidebar
								hideSidebar={() => setHasSidebar(false)}
							/>
						</>
					)}
				</PhotosetStepGridContainer>

				{currentPhotosetSubRoute === 'qa' && (
					<PageFooter
						centerSlot={
							<p>
								{t('photoset:editingQaFlags.footer_step_label')}
							</p>
						}
						endSlot={
							<ButtonLegacy
								onClick={() =>
									setShowConfirmationModal(
										!showConfirmationModal
									)
								}
								type='button'
								variant='primarySuccess'
							>
								{isUpdateStepToEditingQaCompleted && (
									<Spinner size={20} />
								)}
								{t('common:done')}
								{!isUpdateStepToEditingQaCompleted && (
									<CheckIcon />
								)}
							</ButtonLegacy>
						}
						startSlot={
							<ButtonLegacy
								disabled={isUpdateStepToEditingQaSelection}
								onClick={() =>
									onUpdateStepToEditingQaSelection({
										photosetId: urlParams.id,
										imagesIds: []
									})
								}
								type='button'
								variant='tertiary'
							>
								{isUpdateStepToEditingQaSelection && (
									<Spinner size={20} />
								)}
								{!isUpdateStepToEditingQaSelection && (
									<ArrowLeftIcon />
								)}
								{t('photoset:editingQaFlags.back_cta')}
							</ButtonLegacy>
						}
					/>
				)}
			</PhotosetStepWrapper>
		</>
	)
}

PhotosetEditingQaFlagsStep.displayName = 'PhotosetEditingQaFlagsStep'
